import { AdvancedMarker, useAdvancedMarkerRef } from "@vis.gl/react-google-maps";
import React, { FC, useCallback, useMemo } from "react";
import InstantApprovalInfoBox from "../../location/map/InstantApprovalInfoBox";
import RequestSpaceInfoBox from "../../location/map/RequestSpaceInfoBox";
import AtCapacityInfoBox from "../../location/map/AtCapacityInfoBox";
import Money from "js-money";
import { DAYS_PER_MONTH } from "../../constants/securspace-constants";
import type { Supplier } from "../../../types/Supplier";
import LocationPreviewMarker from "./LocationPreviewMarker";
import { useFeatureFlags } from "../../../hooks/routing/useFeatureFlags";

interface ILocationMarkerProps {
  location: Supplier;
  onLocationSelect: (location: Supplier) => void;
  setMarkerRef: (marker: google.maps.marker.AdvancedMarkerElement | null, key: string) => void;
  highlightedLocation?: Supplier | null;
  setHighlightedLocation: (location: Supplier | null) => void;
  hover?: boolean;
}

const LocationMarker: FC<ILocationMarkerProps> = (props) => {
  const { location, onLocationSelect, setMarkerRef, hover, highlightedLocation, setHighlightedLocation } = props;
  const { supplierCapacitySchedule } = useFeatureFlags();
  const isAtCapacity = supplierCapacitySchedule && location.hasAvailableCapacityToday === false;

  const [setLocalMarkerRef, localMarkerRef] = useAdvancedMarkerRef();
  /**
   * Callback ref to set the marker reference in the parent component
   * Adds custom property `hasInstantApproval` to the marker for easier identification
   */
  const ref = useCallback(
    (marker: google.maps.marker.AdvancedMarkerElement) => {
      if (marker) {
        (marker as any).locationId = location.locationId;
        (marker as any).hasInstantApproval = location.hasInstantApproval;
      }
      setMarkerRef(marker, location.locationId);
      setLocalMarkerRef(marker);
    },
    [setMarkerRef, setLocalMarkerRef, location]
  );

  const position = useMemo(() => new google.maps.LatLng(
    parseFloat(location.addressLatitude),
    parseFloat(location.addressLongitude)
  ), [location]);

  const formattedPricePerDay = useMemo(() => {
    const { pricePerDay, pricePerMonth } = location;
    const currencyType = Money.USD;
    // convert to money objects if exists
    const pricePerDayMoney = pricePerDay && new Money(pricePerDay, currencyType);
    const pricePerMonthToPricePerDayMoney = pricePerMonth && (new Money(pricePerMonth, currencyType)).divide(DAYS_PER_MONTH);
    // grab price per day if it exists first, then converted monthly price if not
    const actualPricePerDay = pricePerDayMoney || pricePerMonthToPricePerDayMoney;
    if (actualPricePerDay) {
      // no actual function to do ceil with money object unfortunately, so split decimals and add one if greater than 0

      // eslint-disable-next-line prefer-const
      let [pricePerDayMoniesNoDecimal, pricePerDayDecimals] = actualPricePerDay.toString().split('.');
      if (pricePerDayDecimals && parseInt(pricePerDayDecimals, 10) > 0) {
        pricePerDayMoniesNoDecimal = (parseInt(pricePerDayMoniesNoDecimal, 10) + 1).toString();
      }
      return `${currencyType.symbol}${pricePerDayMoniesNoDecimal}`;
    }
    return '';
  }, [location]);

  const handleInfoBoxClick: (e: React.MouseEvent) => void = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    const isCurrentlyHighlighted = highlightedLocation?.locationId === location.locationId;
    setHighlightedLocation(isCurrentlyHighlighted ? null : location);
  }, [setHighlightedLocation, highlightedLocation, location]);

  return (
    <>
      <AdvancedMarker
        position={position}
        ref={ref}
        // centre the price box on the pin
        anchorLeft="-50%"
        anchorTop="-50%"
        // Newer vis.gl sets pointer-events:none on content unless the marker is clickable,
        // which swallows the price box's onClick
        clickable
      >
        {
          isAtCapacity ?
            <AtCapacityInfoBox
              formattedPrice={formattedPricePerDay}
              listHover={hover}
              onClick={handleInfoBoxClick}
            />
            : location.hasInstantApproval ?
            <InstantApprovalInfoBox
              formattedPrice={formattedPricePerDay}
              listHover={hover}
              onClick={handleInfoBoxClick}
            />
            :
            <RequestSpaceInfoBox
              formattedPrice={formattedPricePerDay}
              listHover={hover}
              onClick={handleInfoBoxClick}
            />
        }
      </AdvancedMarker>
      {
        highlightedLocation?.locationId === location.locationId && localMarkerRef && <LocationPreviewMarker
          anchor={localMarkerRef}
          onClick={onLocationSelect}
          location={location}
          showVisibilityChip={location.hasInstantApproval}
        />
      }
    </>
  );
}

export default LocationMarker;